import { ICON__chevron_down } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { UTILS__generation } from '@/utils/UTILS__generation';
import { cn } from '@/utils/utils_client';
import { DTO__generation_tool } from '@shared/dto/generation/DTO__generation_tool';
import { DTO__generation_tool_grid2d_view } from '@shared/dto/generation/DTO__generation_tool_grid2d_view';
import { ENUM__generation_output_type } from '@shared/dto/generation/graph_invoke/_templates/_enums/ENUM__generation_output_type';
import { FN_GENERATION_TOOL__create_map__image_edit } from '@shared/dto/generation/graph_invoke/_templates/image_edit/FN_GENERATION_TOOL__create_map__image_edit';
import { FN_GENERATION_TOOL__create_map__vision__color_map } from '@shared/dto/generation/graph_invoke/_templates/vision__color_map/FN_GENERATION_TOOL__create_map__vision__color_map';
import { FN_GENERATION_TOOL__create_map__line__canny } from '@shared/dto/generation/graph_invoke/_templates/vision__line__canny/FN_GENERATION_TOOL__create_map__line__canny';
import { MAP__tools_by_name } from '@shared/types/types';
import React from 'react';

interface PROPS {
  is_readonly?: boolean;
}

export function G_DROPDOWN__generation_tool({ is_readonly = false }: PROPS) {
  /*
   * FIELDS
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled, g_tools__output_type } = use_slice_ui(['g_general__ui_disabled', 'g_tools__output_type']);

  const is_disabled = g_general__ui_disabled || is_readonly;

  /*
   *
   */
  let { graph } = generation_current.tool;

  /*
   * EVENTS
   */

  async function on_selected__tool_view(tool: DTO__generation_tool, tool_view: DTO__generation_tool_grid2d_view) {
    if (!generation_current.tool.grid2d_views_map.has(tool_view.name)) return;

    let tool_new = generation_current.tool.clone();
    tool_new.view__set_current(tool_view.name);

    await UTILS__generation.update_tool(tool_new);
  }

  async function on_selected__tool(tool: DTO__generation_tool) {
    await UTILS__generation.update_tool(tool);
  }

  /*
   *
   */

  function render_tools() {
    let out = null;

    switch (g_tools__output_type) {
      case ENUM__generation_output_type.IMAGE:
        out = render_block(FN_GENERATION_TOOL__create_map__image_edit(graph));
        break;
      case ENUM__generation_output_type.IMAGE_CONTROL__CANNY:
        out = render_block(FN_GENERATION_TOOL__create_map__line__canny(graph));
        break;
      case ENUM__generation_output_type.IMAGE_CONTROL__COLOR_MAP:
        out = render_block(FN_GENERATION_TOOL__create_map__vision__color_map(graph));
        break;
      default:
        // do nothing
        break;
    }

    return out;
  }

  function render_block(map: MAP__tools_by_name) {
    if (map.is_empty()) return null;

    return map.values().map(render_item);
  }

  function render_item(tool: DTO__generation_tool) {
    let is_current = tool._id == generation_current.tool._id;
    let is_single_view = tool.grid2d_views_map.size() == 1;

    let need_render_views = is_current && !is_single_view;

    return (
      <React.Fragment key={`graph_template_${tool._id}`}>
        <UI_PRIMITIVE__dropdown_menu_item
          values_selected={[tool.name]}
          class_name_hovered_selected="bg-component-item-selected"
          className={cn('pcursor-pointer h-5 text-xs text-component-item-foreground')}
          value={''}
          disabled={is_current || is_disabled}
          on_item_selected={() => void on_selected__tool(tool)}
        >
          {tool.name}
        </UI_PRIMITIVE__dropdown_menu_item>

        {need_render_views && tool.grid2d_views_map.values().map(tool_view => render_tool_view(tool, tool_view))}
      </React.Fragment>
    );
  }

  function render_tool_view(tool: DTO__generation_tool, tool_view: DTO__generation_tool_grid2d_view) {
    let is_current_tool = tool._id == generation_current.tool._id;
    let is_current_tool_view = tool_view.name == generation_current.tool.grid2d_view_current_name;

    let is_current = is_current_tool && is_current_tool_view;

    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={`tool_view_${tool._id}_${tool_view.name}`}
        values_selected={[tool.name]}
        disabled={is_current || is_disabled}
        class_name_hovered_selected="bg-component-item-selected"
        className={cn('px-4 cursor-pointer h-5 text-left text-xs text-component-item-foreground')}
        value={''}
        on_item_selected={() => void on_selected__tool_view(tool, tool_view)}
      >
        <span className="w-0.5">{is_current && '•'}</span> {tool_view.name}
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  function render_tool_name_current() {
    let out = generation_current.tool.name;

    if (generation_current.tool.grid2d_views_map.size() == 1) return out;

    out = `${out}: ${generation_current.tool.grid2d_view_current_name}`;

    return out;
  }

  /*
   * RENDERS
   */

  /*
   * HTML
   */

  if (generation_current.is_stub()) return <PLACEHOLDER_bar_line />;

  return (
    <>
      <div className="flex flex-col min-h-6 h-6 gap-1 p-1 flex-shrink-0 border-b border-component-border">
        <UI_PRIMITIVE__dropdown_menu modal={false}>
          <UI_PRIMITIVE__dropdown_menu_trigger asChild>
            <UI_PRIMITIVE__button
              size="full-4"
              disabled={is_disabled}
              className={`info__dropdown_graph_template relative !text-bar-item-foreground justify-start text-xs rounded-sm truncate`}
              variant="outline-on-component"
            >
              <span className="w-full px-4 truncate text-left">Tool: {render_tool_name_current()}</span>
              {!is_disabled && <ICON__chevron_down className="!size-2 absolute right-3 top-3.3" />}
            </UI_PRIMITIVE__button>
          </UI_PRIMITIVE__dropdown_menu_trigger>

          <UI_PRIMITIVE__dropdown_menu_content
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg p-0 !bg-component"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            {render_tools()}
          </UI_PRIMITIVE__dropdown_menu_content>
        </UI_PRIMITIVE__dropdown_menu>
      </div>
    </>
  );
}
